import { useEffect } from "react";

import { first } from "lodash/fp";
import { useParams } from "react-router";

import useSearchMeetingsQuery from "@app/analytics/hooks/meetings/useSearchMeetingsQuery";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import type { SearchType } from "@app/analytics/MeetingReport/types";
import type {
  NoteSearchResult,
  TranscriptSearchResult,
} from "@app/analytics/MeetingReport/types";
import {
  isKeywordSearch,
  removeKeywordSearchPrefix,
  transformNoteSearchResults,
  transformTranscriptSearchResults,
} from "@app/analytics/MeetingReport/utils";
import type { SearchRequestParams } from "@app/analytics/types";

import useMeetingReport from "./useMeetingReport";
import useMeetingReportSessionNotes from "./useMeetingReportSessionNotes";

const getQueryFromSearchTerm = (
  searchTerm: string,
  searchType: SearchType | null
) => {
  // alternative here is a "manual" search, which we can't currently rely on the BE to do
  // correctly because this is the type of search we do on the coaching tab, where we want
  // to only match on exact phrase (e.g., "um" matches "um" but not "momentum")
  // so if that's the type of search we're doing, don't return a search term so the search
  // query won't fire
  if (searchType !== "default") return;

  return isKeywordSearch(searchTerm)
    ? removeKeywordSearchPrefix(searchTerm)
    : searchTerm;
};

const EMPTY_SEARCH_RESULTS: (NoteSearchResult | TranscriptSearchResult)[] = [];

/** This hook is not to be used on its own. To use the search functionality, use the `useSearch` hook. */
const useMeetingReportSearch = (opts: {
  enabled: boolean;
  searchNotes: boolean;
  searchTranscript: boolean;
}) => {
  const { sessionId } = useParams();
  const {
    meetingStartTime,
    meetingEndTime,
    isLoading: isMeetingReportInitialLoading,
  } = useMeetingReport(opts);

  const {
    summary,
    turns,
    isLoading: isTranscriptLoading,
    wordDelimiter,
  } = useTranscript(opts);

  const { notes, isInitialLoading: isNotesInitialLoading } =
    useMeetingReportSessionNotes(opts);

  const { searchTerm, searchType, setSearchResults } = useSearchStore(
    (state) => ({
      searchTerm: state.searchTerm,
      searchType: state.searchType,
      setSearchResults: state.setSearchResults,
    })
  );

  const params: Partial<SearchRequestParams> = {
    query: getQueryFromSearchTerm(searchTerm, searchType),
    startDate: meetingStartTime,
    endDate: meetingEndTime,
    sessionId,
  };

  const { results, isLoading: isSearching } = useSearchMeetingsQuery(params, {
    enabled: opts.enabled,
  });

  const isContentLoading =
    isMeetingReportInitialLoading ||
    isTranscriptLoading ||
    isNotesInitialLoading;

  const isLoading = isContentLoading || isSearching;

  useEffect(() => {
    if (isLoading || !opts.enabled) return;

    const resultsForReport = first(results)?.matches ?? [];

    const noteSearchResults = opts.searchNotes
      ? transformNoteSearchResults(resultsForReport, summary, notes)
      : EMPTY_SEARCH_RESULTS;

    const transcriptSearchResults = opts.searchTranscript
      ? transformTranscriptSearchResults(
          resultsForReport,
          turns,
          wordDelimiter,
          noteSearchResults.length
        )
      : EMPTY_SEARCH_RESULTS;

    const allSearchResults = [...noteSearchResults, ...transcriptSearchResults];
    setSearchResults(allSearchResults);
  }, [
    isLoading,
    results,
    wordDelimiter,
    opts.enabled,
    opts.searchNotes,
    opts.searchTranscript,
  ]);
};

export default useMeetingReportSearch;
